import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { isNil } from 'ramda';
import { Typography, IconButton, Switch } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import { Skeleton } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { isBlank, isPresent } from 'utils/helperMethods';

import ChatPresenter from 'presenters/ChatPresenter';
import BidRequestPresenter from 'presenters/BidRequestPresenter';
import BuyerPresenter from 'presenters/BuyerPresenter';

import Tooltip from 'sharedComponents/Tooltip';
import Icon from 'sharedComponents/Icon';

import useStyles from './useStyles';

const BuyerCard = props => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    lastEventAt,
    onClick,
    isHightlighted,
    isOnline,
    unreadCount,
    bidRequest,
    buyer,
    onAddToFavorite,
    onRemoveFromFavorite,
    onAddToHidden,
    onRemoveFromHidden,
    isFavorite,
    isHidden,
    chatInfo,
    onToggleAutoFollowupsMessages,
    isEnabledAutoFollowupsMessages,
    isChatUpdating,
  } = props;

  const isBidRequestPresent = isPresent(bidRequest);
  const loanAmmount = isBidRequestPresent ? BidRequestPresenter.readalbeLoanAmmount(bidRequest) : '--';
  const ltv = isBidRequestPresent ? BidRequestPresenter.readableLtv(bidRequest) : '--';
  const city = isBidRequestPresent ? BidRequestPresenter.city(bidRequest) : '--';

  const isBuyerPresent = isPresent(buyer);
  const hasUnreadMessages = !isNil(unreadCount) && unreadCount !== 0;
  const isEnabledChatAutoFollowupsMessages = Boolean(ChatPresenter.enabledAutoFollowupsMessages(chatInfo));

  const handleToggleAutoFollowupsMessages = () => {
    const chatUid = ChatPresenter.firebaseChatUid(chatInfo);
    const attributes = { chat: { enabledAutoFollowupsMessages: !isEnabledChatAutoFollowupsMessages } };
    onToggleAutoFollowupsMessages(chatUid, attributes);
  };

  const stopPropagation = handle => event => {
    event.stopPropagation();
    event.preventDefault();
    handle();
  };

  return (
    <div
      className={clsx(classes.container, { [classes.active]: isHightlighted, [classes.inActive]: !isHightlighted })}
      onClick={onClick}
      role="presentation"
    >
      <div className={classes.upperBar}>
        <div className={classes.buyerInfo}>
          <Typography variant="subtitle1" color="secondary">
            {t('models.App.userTypes.buyer')}
          </Typography>
          <div className={classes.infoItem}>
            {isBuyerPresent ? (
              <Typography variant="h2" noWrap className={classes.name}>
                {BuyerPresenter.fullName(buyer)}
              </Typography>
            ) : (
              <Skeleton width={150} height={30} />
            )}
            {isOnline && (
              <Typography variant="subtitle1" color="inherit" className={classes.onineStatus}>
                {t('components.BuyerCard.online')}
              </Typography>
            )}
          </div>
          <div className={classes.infoItem}>
            <Icon name="watch" />
            <Typography variant="subtitle1" color="secondary" className={classes.date}>
              {isBlank(lastEventAt) ? t('models.App.Chat.away') : lastEventAt}
            </Typography>
          </div>
          {isPresent(chatInfo) && (
            <div className={classes.autoFollowups}>
              <Tooltip arrow body={t('components.BuyerCard.autoFollowupsTooltip')}>
                <div className={classes.autoFollowupsLabel}>
                  <Typography variant="subtitle1" color="secondary" className={classes.date}>
                    {t('components.BuyerCard.autoFollowupsSwitch')}
                  </Typography>
                  <InfoIcon className={classes.infoIcon} />
                </div>
              </Tooltip>
              <Switch
                size="small"
                checked={isEnabledChatAutoFollowupsMessages}
                onClick={stopPropagation(handleToggleAutoFollowupsMessages)}
                disabled={!isPresent(chatInfo) || isChatUpdating || !isEnabledAutoFollowupsMessages}
              />
            </div>
          )}
        </div>
        <div className={classes.chatControlBar}>
          {isFavorite ? (
            <IconButton className={classes.iconButton} onClick={stopPropagation(onRemoveFromFavorite)}>
              <Icon name="star" className={classes.starIcon} />
            </IconButton>
          ) : (
            <IconButton className={classes.iconButton} onClick={stopPropagation(onAddToFavorite)}>
              <Icon name="contourStar" className={classes.inactiveIcon} />
            </IconButton>
          )}
          {isHidden ? (
            <IconButton className={classes.iconButton} onClick={stopPropagation(onRemoveFromHidden)}>
              <Icon name="visibleOff" className={classes.icon} />
            </IconButton>
          ) : (
            <IconButton className={classes.iconButton} onClick={stopPropagation(onAddToHidden)}>
              <Icon name="visible" className={classes.inactiveIcon} />
            </IconButton>
          )}
          {hasUnreadMessages && (
            <div className={classes.unreadMessagesBadge}>
              <Typography variant="subtitle1" className={classes.unredMessagesText}>
                {`${unreadCount}`}
              </Typography>
            </div>
          )}
        </div>
      </div>
      <div className={classes.bottomBar}>
        <div className={classes.bottomBarLeft}>
          <Typography variant="subtitle1" color="secondary">
            {t('models.App.BidRequest.loanAmmount')}
          </Typography>
          <h2 className={classes.additionalText}>{loanAmmount}</h2>
        </div>
        <div className={classes.bottomBarCenter}>
          <Typography variant="subtitle1" color="secondary">
            {t('models.App.BidRequest.ltv')}
          </Typography>
          <h2 className={classes.additionalText}>{ltv}</h2>
        </div>
        <div className={classes.bottomBarRight}>
          <Typography variant="subtitle1" color="secondary">
            {t('models.App.BidRequest.city')}
          </Typography>
          <h2 className={classes.additionalText}>{city}</h2>
        </div>
      </div>
    </div>
  );
};

BuyerCard.propTypes = {
  isHightlighted: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  lastEventAt: PropTypes.string,
  unreadCount: PropTypes.number,
  isOnline: PropTypes.bool,
  bidRequest: BidRequestPresenter.shape(),
  chatInfo: ChatPresenter.shape(),
  buyer: BuyerPresenter.shape(),
  onAddToFavorite: PropTypes.func.isRequired,
  onRemoveFromFavorite: PropTypes.func.isRequired,
  onAddToHidden: PropTypes.func.isRequired,
  onRemoveFromHidden: PropTypes.func.isRequired,
  isFavorite: PropTypes.bool,
  isHidden: PropTypes.bool,
  onToggleAutoFollowupsMessages: PropTypes.func.isRequired,
  isChatUpdating: PropTypes.bool.isRequired,
  isEnabledAutoFollowupsMessages: PropTypes.bool.isRequired,
};

BuyerCard.defaultProps = {
  isOnline: false,
  lastEventAt: null,
  chatInfo: {},
  isFavorite: false,
  isHidden: false,
  unreadCount: 0,
  isHightlighted: false,
  bidRequest: {},
  buyer: {},
};

export default BuyerCard;



// WEBPACK FOOTER //
// ./src/sharedComponents/BuyerCard/BuyerCard.js